/**
 * Nutrient math — the single source of truth for building, summing, and scaling
 * Nutrients objects on the web app. Port of the mobile utils/nutrients.ts; keep
 * the two in sync so an entry logged on web is byte-identical to one from phone.
 *
 * Every construction/scale site MUST go through these helpers instead of
 * hand-enumerating fields. Hand-written field lists silently drop optional
 * nutrients (fiberG, sugarsG, saturatedFatG, calciumMg, vitaminKMcg, …) whenever
 * one is missed, which is how daily summaries end up reading 0 for a nutrient the
 * per-food breakdown clearly has. Iterating the canonical key list makes that
 * class of bug impossible.
 */

import type { Nutrients, NutrientCoverage } from '@/types'
import { isKnownIronMg, parseIronInput, roundIronMg } from '../../../shared/iron'

/** Core nutrients — always present on a logged food. */
export const CORE_NUTRIENT_KEYS = [
  'sodiumMg',
  'potassiumMg',
  'phosphorusMg',
  'proteinG',
] as const

/** Optional nutrients — present only when tracked/available. */
export const OPTIONAL_NUTRIENT_KEYS = [
  'calories',
  'carbsG',
  'sugarsG',
  'fiberG',
  'fatG',
  'saturatedFatG',
  'magnesiumMg',
  'calciumMg',
  'vitaminKMcg',
  'ironMg',
  'oxalateMg',
] as const

/** Every nutrient key in Nutrients, core first. */
export const NUTRIENT_KEYS = [...CORE_NUTRIENT_KEYS, ...OPTIONAL_NUTRIENT_KEYS] as const

export type NutrientKey = (typeof NUTRIENT_KEYS)[number]

/** Keys rounded to one decimal place (grams / mcg); everything else to a whole number. */
const DECIMAL_KEYS = new Set<NutrientKey>([
  'proteinG',
  'carbsG',
  'sugarsG',
  'fiberG',
  'fatG',
  'saturatedFatG',
  'vitaminKMcg',
])

/**
 * 4.39: keys stored at two decimals (iron, mg). The whole-number rounding would
 * store a 0.04 mg food as a measured 0.
 */
export const TWO_DECIMAL_KEYS = new Set<NutrientKey>(['ironMg'])

/**
 * 4.39: keys summed complete-only — emitted only when EVERY input has a known value
 * (shared/iron.ts isKnownIronMg), so a stored entry never carries a partial sum as
 * its total. Unknown is never 0.
 */
export const COMPLETE_ONLY_KEYS = new Set<NutrientKey>(['ironMg'])

/** Display / form precision for a key: 2 (iron), 1 (grams / mcg) or 0. */
export function nutrientDecimals(key: NutrientKey): 0 | 1 | 2 {
  if (TWO_DECIMAL_KEYS.has(key)) return 2
  return DECIMAL_KEYS.has(key) ? 1 : 0
}

/** Round a single nutrient value with the app's per-key convention. */
export function roundNutrient(key: NutrientKey, value: number): number {
  if (TWO_DECIMAL_KEYS.has(key)) return Math.round(value * 100) / 100
  return DECIMAL_KEYS.has(key) ? Math.round(value * 10) / 10 : Math.round(value)
}

/**
 * Format one amount for a per-nutrient drill-down — a contributing row, or the
 * header total those rows are meant to add up to.
 *
 * Rounding every row to a whole number makes the list contradict its own
 * header: six foods at ~1.6g each read "2g" under a "10g" total the user
 * can only add up to 9. So anything under 10 keeps one decimal while larger
 * values stay whole, and the SAME helper formats the total — the column adds
 * up on screen. Whole values are left bare ("3", not "3.0").
 *
 * Mirror of the mobile utils/nutrients.ts helper; keep the two in sync.
 */
export function formatNutrientAmount(value: number): string {
  const oneDecimal = Math.round(value * 10) / 10
  return (Math.abs(oneDecimal) >= 10 ? Math.round(oneDecimal) : oneDecimal).toLocaleString()
}

type NutrientLike = Partial<Nutrients> | null | undefined

/**
 * Sum a list of nutrient objects. An optional key is emitted only when at least
 * one input defines it (never `undefined` — Firestore rejects that). Core keys
 * are always emitted (default 0). Values are summed raw; scale with
 * {@link scaleNutrients} when you need rounded, storable output.
 *
 * 4.39 iron is the exception (COMPLETE_ONLY_KEYS): `null` inputs are dropped, and
 * `ironMg` is emitted only when the remaining list is non-empty and every input's
 * iron is known — `[]` proves nothing, one missing / negative / NaN / string value
 * makes the sum unknown. It is rounded to 2 decimals (0.1 + 0.2 float noise).
 */
export function sumNutrients(list: NutrientLike[]): Nutrients {
  const acc: Record<string, number> = {}
  const seen = new Set<NutrientKey>(CORE_NUTRIENT_KEYS)
  for (const key of CORE_NUTRIENT_KEYS) acc[key] = 0

  const present = list.filter((n): n is Partial<Nutrients> => !!n)
  for (const n of present) {
    for (const key of NUTRIENT_KEYS) {
      if (COMPLETE_ONLY_KEYS.has(key)) continue
      const v = (n as Record<string, number | undefined>)[key]
      if (v != null) {
        acc[key] = (acc[key] || 0) + v
        seen.add(key)
      }
    }
  }
  const iron = ironCoverage(present)

  const out: Record<string, number> = {}
  for (const key of NUTRIENT_KEYS) {
    if (key === 'ironMg') {
      if (iron.total > 0 && iron.known === iron.total) out[key] = iron.value
      continue
    }
    if (seen.has(key)) out[key] = acc[key] || 0
  }
  return out as unknown as Nutrients
}

/**
 * 4.39: iron coverage of a list of nutrient objects. `total` counts the non-null
 * inputs, `known` those whose `ironMg` passes the validity rule, `value` is the
 * known subtotal at 2 decimals (0 when nothing is known — check `known` first and
 * never show that 0 as a measurement). Callers pass only the entries that carry
 * nutrients (pure fluids and Apple Health water rows are left out).
 */
export function ironCoverage(list: readonly NutrientLike[]): NutrientCoverage {
  let known = 0
  let total = 0
  let sum = 0
  for (const n of list) {
    if (!n) continue
    total += 1
    const v = (n as Record<string, unknown>).ironMg
    if (isKnownIronMg(v)) {
      known += 1
      sum += v
    }
  }
  return { known, total, value: roundIronMg(sum) }
}

/** Structural diary entry, as far as iron coverage is concerned (mobile and web entries both fit). */
export interface CoverageEntryLike {
  isFluid?: boolean
  source?: string
  nutrients?: NutrientLike
}

/**
 * 4.39: the entries a day's (or meal's) iron coverage counts — every entry that
 * carries nutrients, except pure-fluid rows (a fluid with no core nutrients, the
 * web summary's `isPureFluid` rule) and Apple Health water rows (`source: 'healthkit'`).
 */
export function coverageEntriesOf<T extends CoverageEntryLike>(entries: readonly T[]): T[] {
  return entries.filter((e) => {
    const n = e.nutrients
    if (!n || e.source === 'healthkit') return false
    const pureFluid = !!e.isFluid && !(n.sodiumMg || n.potassiumMg || n.phosphorusMg || n.proteinG)
    return !pureFluid
  })
}

/** 4.39: {@link ironCoverage} over {@link coverageEntriesOf} — the day / meal coverage. */
export function dayIronCoverage(entries: readonly CoverageEntryLike[]): NutrientCoverage {
  return ironCoverage(coverageEntriesOf(entries).map((e) => e.nutrients))
}

/**
 * 4.39: a period's iron average from per-day coverage — COMPLETE days only (every entry
 * with nutrients has a value, n = m > 0). `loggedDays` counts the days with at least one
 * entry that carries nutrients (m > 0); `value` is null when no day is complete. Shown as
 * "average of {completeDays} of {loggedDays} logged days"; a partial day never enters it.
 */
export function ironPeriodAverage(days: readonly NutrientCoverage[]): {
  value: number | null
  completeDays: number
  loggedDays: number
} {
  let loggedDays = 0
  let completeDays = 0
  let sum = 0
  for (const d of days) {
    if (!(d.total > 0)) continue
    loggedDays += 1
    if (d.known !== d.total) continue
    completeDays += 1
    sum += d.value
  }
  return { value: completeDays > 0 ? roundIronMg(sum / completeDays) : null, completeDays, loggedDays }
}

/**
 * Scale a nutrient object by `factor`, rounding each key per convention. Only
 * keys present (non-null) on the input are emitted; core keys are always emitted.
 * Use for servings multiplication and per-serving division (factor = 1/servings).
 */
export function scaleNutrients(n: NutrientLike, factor: number): Nutrients {
  const src = (n || {}) as Record<string, number | undefined>
  const out: Record<string, number> = {}
  for (const key of CORE_NUTRIENT_KEYS) {
    out[key] = roundNutrient(key, (src[key] || 0) * factor)
  }
  for (const key of OPTIONAL_NUTRIENT_KEYS) {
    const v = src[key]
    if (COMPLETE_ONLY_KEYS.has(key)) {
      // Unknown iron stays absent; a string or negative is never scaled into a number.
      if (isKnownIronMg(v)) out[key] = roundNutrient(key, v * factor)
      continue
    }
    if (v != null) out[key] = roundNutrient(key, v * factor)
  }
  return out as unknown as Nutrients
}

/**
 * Rescale one editable form field (a nutrient or fluid amount held as text)
 * for a servings/quantity change without compounding rounding. Mirror of the
 * mobile utils/nutrients.ts helper — keep the two identical.
 *
 * `base` is the unrounded value calibrated at `baseServings`. If the text still
 * equals what `base` formats to at `fromServings`, the field is untouched and
 * scales from `base`; otherwise the user typed their own number at
 * `fromServings` and `base` is recalibrated from it. Empty / non-numeric text
 * is left alone.
 *
 * Never scale the rounded on-screen text itself: that compounds rounding every
 * step (potassium 350 at 1 serving → 0.01 → 0.5 came out 200, not 175).
 */
export function rescaleFormValue(
  shown: string,
  base: number | undefined,
  baseServings: number,
  fromServings: number,
  toServings: number,
  decimals: 0 | 1 | 2,
): { shown: string; base: number | undefined } {
  if (!shown || !(baseServings > 0) || !(fromServings > 0)) return { shown, base }
  const format = (v: number) => formatAtDecimals(v, decimals)
  let calibrated: number
  if (base != null && shown === format((base * fromServings) / baseServings)) {
    calibrated = base
  } else {
    // 2-decimal fields (iron) accept a decimal comma ("0,04"); the others are unchanged.
    const typed = decimals === 2 ? parseFloat(shown.replace(/,/g, '.')) : parseFloat(shown)
    if (!Number.isFinite(typed)) return { shown, base }
    calibrated = (typed * baseServings) / fromServings
  }
  return { shown: format((calibrated * toServings) / baseServings), base: calibrated }
}

function formatAtDecimals(v: number, decimals: 0 | 1 | 2): string {
  if (decimals === 2) return String(Math.round(v * 100) / 100)
  return String(decimals ? Math.round(v * 10) / 10 : Math.round(v))
}

/**
 * 4.39: the value an editor SAVES for one field (used for iron). The text on screen
 * is rounded, so saving it would re-round the stored value, and scaled text would be
 * stored rounded; unchanged rounded text does not even mean an unchanged quantity.
 *
 *   blank                                          → undefined (remove the key)
 *   text === format(base × servings / baseServings) → that scaled baseline, rounded
 *                                                    to `decimals` (not the text)
 *   anything else                                  → the typed number (decimal comma
 *                                                    accepted), rounded to `decimals`
 *   non-numeric, non-finite or negative            → undefined
 *
 * `base` is the unrounded value calibrated at `baseServings` (see rescaleFormValue).
 * Mirror of the mobile utils/nutrients.ts helper — keep the two identical.
 */
export function committedFormValue(
  shown: string,
  base: number | undefined,
  baseServings: number,
  servings: number,
  decimals: 0 | 1 | 2,
): number | undefined {
  const text = String(shown ?? '').trim()
  if (!text) return undefined
  const round = (v: number) => {
    const f = 10 ** decimals
    return Math.round(v * f) / f
  }
  if (base != null && Number.isFinite(base) && base >= 0 && baseServings > 0 && servings > 0) {
    const scaled = (base * servings) / baseServings
    if (text === formatAtDecimals(scaled, decimals)) return round(scaled)
  }
  const typed = parseIronInput(text)
  if (typed === undefined) return undefined
  return round(typed)
}

/**
 * {@link rescaleFormValue} over a whole nutrient form (text keyed by nutrient
 * key), at each key's display precision. Returns the new text and the
 * recalibrated unrounded bases to scale from next time.
 */
export function rescaleNutrientForm(
  values: Record<string, string>,
  base: Record<string, number | undefined>,
  baseServings: number,
  fromServings: number,
  toServings: number,
): { values: Record<string, string>; base: Record<string, number | undefined> } {
  const nextValues: Record<string, string> = {}
  const nextBase: Record<string, number | undefined> = { ...base }
  for (const [key, shown] of Object.entries(values)) {
    const decimals = nutrientDecimals(key as NutrientKey)
    const r = rescaleFormValue(shown, base[key], baseServings, fromServings, toServings, decimals)
    nextValues[key] = r.shown
    nextBase[key] = r.base
  }
  return { values: nextValues, base: nextBase }
}

/**
 * Sum the nutrients of a list of food items (anything with a `.nutrients`).
 * Absent optional nutrients are omitted, present ones summed.
 */
export function totalsFromFoods(
  foods: Array<{ nutrients?: NutrientLike } | null | undefined>,
): Nutrients {
  return sumNutrients(foods.map((f) => f?.nutrients))
}

/**
 * Merge two nutrient objects, preferring `primary`. Core + any key present on
 * `primary` win; keys only present on `fallback` are backfilled. Used when a
 * server meal-total omits an optional nutrient that the per-food breakdown
 * carries, so top-level `nutrients` (and daily summaries) aren't zeroed.
 */
export function mergeNutrients(primary: NutrientLike, fallback: NutrientLike): Nutrients {
  const p = (primary || {}) as Record<string, number | undefined>
  const f = (fallback || {}) as Record<string, number | undefined>
  const out: Record<string, number> = {}
  for (const key of CORE_NUTRIENT_KEYS) {
    out[key] = p[key] ?? f[key] ?? 0
  }
  for (const key of OPTIONAL_NUTRIENT_KEYS) {
    if (COMPLETE_ONLY_KEYS.has(key)) {
      // 4.39: only a known iron value is carried; the fallback (summed foods) is complete-only.
      const v = isKnownIronMg(p[key]) ? p[key] : f[key]
      if (isKnownIronMg(v)) out[key] = v
      continue
    }
    const v = p[key] != null ? p[key] : f[key]
    if (v != null) out[key] = v
  }
  return out as unknown as Nutrients
}
